import { type Ref, useEffect } from 'react'
import { DeepPartial } from '@apollo/client/utilities'
import {
  Alert,
  Card,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  Switch,
} from 'antd'

import type { BaseFormValues } from './formTypes'
import { layout } from '~/constants/forms'
import {
  ProductAvailability,
  ProductDisplayCategory,
  ProductType,
  type SchemeProductInput,
} from '~/gql/generated/graphql'
import {
  ProductTypeSelect,
  RewardTypeSelect,
  ValidityIntervalSelect,
} from './components'
import { CurrencyInput } from '~/components/shared/numberInputs'
import { currencyFormItemConversionProps } from './utils'
import { capitalize } from 'lodash'
import { SchemeId } from '~/constants/domain'

export const ProductDisplayCategoryLabels: Record<
  ProductDisplayCategory,
  string
> = {
  [ProductDisplayCategory.minute_pass]: 'Minute Pass',
  [ProductDisplayCategory.special_pass]: 'Special Pass',
}

type Props = {
  loading?: boolean
  initialValues?: DeepPartial<SchemeProductInput>
  formRef: Ref<FormInstance<BaseFormValues>>
}
const ProductDetailsForm = ({ initialValues, loading, formRef }: Props) => {
  const [form] = Form.useForm()

  const isProtected = Form.useWatch('is_protected', form)
  const productType = Form.useWatch('product_type', form)

  const isMinuteProduct = [
    ProductType.period_pass,
    ProductType.bundle,
  ].includes(productType)

  // Clear `role_whitelist` if `is_protected` is un-set
  useEffect(() => {
    // Not if its undefined tho, or it affects the initial values
    if (isProtected === false) {
      form.setFieldValue('role_whitelist', [])
    }
  }, [isProtected, form])

  const inGM = initialValues?.scheme_id === SchemeId.greater_manchester

  return (
    <Form<BaseFormValues>
      {...layout}
      layout="horizontal"
      form={form}
      name="productForm"
      initialValues={initialValues}
      disabled={loading}
      ref={formRef}
    >
      <Card title="Product details">
        {!inGM && productType === ProductType.bundle && (
          <Alert
            showIcon
            icon="🥺"
            type="warning"
            message="Please don't create new bundles"
            description="You probably want 'Period pass' with 'Minute pass' display category"
            style={{ marginBottom: 20 }}
            action="Ok"
            onClick={() => {
              form.setFieldValue('product_type', ProductType.period_pass)
              form.setFieldValue(
                'display_category',
                ProductDisplayCategory.minute_pass,
              )
            }}
          />
        )}

        <Form.Item name="scheme_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          name="product_type"
          label="Product type"
          rules={[{ required: true, message: 'Please choose a product type' }]}
        >
          <ProductTypeSelect disabled={Boolean(initialValues?.product_type)} />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please specify a name' }]}
          tooltip="This is visible in the app"
        >
          <Input disabled={!productType} />
        </Form.Item>
      </Card>

      {productType === ProductType.payg ? (
        <Form.Item
          name="amount"
          label="Price"
          {...currencyFormItemConversionProps}
          hidden
        >
          <InputNumber disabled={!productType} />
        </Form.Item>
      ) : (
        <Card title="Product settings">
          {productType === ProductType.period_pass && (
            <>
              <Form.Item
                name="validity"
                label="Valid for"
                rules={[
                  {
                    required: true,
                    message: 'Please specify a validity period',
                  },
                ]}
              >
                <ValidityIntervalSelect />
              </Form.Item>
              <Form.Item name="availability" label="Availability">
                <Select
                  allowClear
                  options={Object.keys(ProductAvailability).map(value => ({
                    label: capitalize(value),
                    value: ProductAvailability[value],
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="display_category"
                label="Display Category"
                tooltip="This configures whether to display this product as a regular product that gives minutes (e.g. 5 minute pass) vs. a special pass (e.g. day pass) in the rider app."
                rules={[
                  {
                    required: true,
                    message: 'Please specify a display category.',
                  },
                ]}
              >
                <Select
                  allowClear
                  options={Object.values(ProductDisplayCategory).map(value => ({
                    label: ProductDisplayCategoryLabels[value],
                    value,
                  }))}
                />
              </Form.Item>
            </>
          )}

          <>
            <Form.Item
              name="amount"
              label="Price"
              {...currencyFormItemConversionProps}
              rules={[{ required: true, message: 'Please specify a price' }]}
            >
              <CurrencyInput disabled={!productType} />
            </Form.Item>

            {isMinuteProduct && (
              <>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[
                    {
                      required: true,
                      message: 'Please specify a number of time units',
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    step={10}
                    addonAfter={'minutes'}
                    disabled={!productType}
                  />
                </Form.Item>

                <Form.Item
                  name="fulfill_rewards"
                  label="Rewards ⭐"
                  help="Do any rewards fulfil this product?"
                >
                  <RewardTypeSelect mode="multiple" disabled={!productType} />
                </Form.Item>
              </>
            )}

            {productType === ProductType.subscription_pass && (
              <>
                <Form.Item
                  name="billable_journey_cap"
                  label="Billable journes per day"
                >
                  <InputNumber min={0} max={10} />
                </Form.Item>
                <Form.Item
                  name="billable_journey_cap_sub_period"
                  label="Period"
                  hidden
                >
                  <Input value="P1D" />
                </Form.Item>
              </>
            )}
            <Form.Item
              name="waive_out_of_zone_parking_charge"
              label="Waive OOB fine?"
              help="Allow customers to park outside of a bay without being fined"
            >
              <Switch
                defaultChecked={
                  initialValues?.waive_out_of_zone_parking_charge ?? false
                }
              />
            </Form.Item>
          </>
        </Card>
      )}

      <Card title="Visibility">
        {productType !== ProductType.payg && (
          <Form.Item
            name="is_discoverable"
            label="Discoverable 🎟️"
            help="Viewable in the app. Hidden products can be used for vouchers and bonuses."
          >
            <Switch defaultChecked={initialValues?.is_discoverable ?? true} />
          </Form.Item>
        )}

        <Form.Item
          name="is_protected"
          label="Protected 🔒"
          help="Available only to users with the specified roles"
        >
          <Switch
            defaultChecked={initialValues?.is_protected ?? false}
            disabled={!productType}
          />
        </Form.Item>

        <Form.Item
          name="role_whitelist"
          label="Roles allowed 🔒"
          rules={[
            {
              required: isProtected,
              message: 'Please select roles who are eligible for this product.',
            },
          ]}
        >
          <Select
            options={[
              { value: 'member', label: 'Member' },
              { value: 'external_stakeholder', label: 'External Stakeholder' },
              { value: 'external_tester', label: 'External Tester' },
            ]}
            mode="multiple"
            disabled={!isProtected || !productType}
          />
        </Form.Item>
      </Card>
      <Card title="Danger zone">
        <Form.Item
          name="is_active"
          label="Active"
          help="De-activating a pass means it can't be used"
          tooltip="You might want to make it non-discoverable first, so it can still be debited by people who have it."
        >
          <Switch
            defaultChecked={initialValues?.is_active ?? false}
            disabled={!productType}
          />
        </Form.Item>
      </Card>
    </Form>
  )
}

export default ProductDetailsForm
